import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Settings, Activity, HardDrive, Server, Cpu, MemoryStick, AlertTriangle, CheckCircle, RefreshCw, Download, Upload, Shield, FileText, User, Calendar, ChevronRight, ChevronDown, TrendingDown, TrendingUp, HardDriveDownload, Plug, XCircle, Wifi, Building2, Save, Bell, KeyRound } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { subscribeToPush, requestNotificationPermission } from '../utils/pwa';
import { ajustesClavesAPI } from '../api/api';
import { revisarNovedades } from '../components/guia/novedades';
import {
  Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler,
} from 'chart.js';
import { Line } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmtFechaHora = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const fmtDolares = (n) => Number(n || 0).toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtLimiteDolares = (n) => {
  const v = Number(n || 0);
  return Number.isInteger(v) ? v.toLocaleString('es-ES') : fmtDolares(v);
};

// "2026-10-01" -> "01/10" (sin pasar por la zona horaria); `restarDias` para
// enseñar el último día del ciclo (ciclo_hasta es el día en que se renueva)
const fmtDiaMes = (iso, restarDias = 0) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(iso || ''));
  if (!m) return '';
  const d = new Date(Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]) - restarDias));
  return `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};

const colorCredito = (pct) => {
  const v = Number(pct) || 0;
  if (v > 90) return 'bg-red-500';
  if (v >= 70) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Crédito del mes de la API de Claude: estimado por el ERP (Anthropic no da el
// saldo a organizaciones individuales). Se edita el crédito mensual y el día de
// renovación; lo gastado sale de las conversaciones de Ayuda IA del ciclo.
const BloqueCreditoClaude = ({ credito, onGuardado }) => {
  const [mensualTxt, setMensualTxt] = useState('');
  const [diaTxt, setDiaTxt] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState(null);
  const [guardadoOk, setGuardadoOk] = useState(false);
  const editadoRef = useRef(false);

  const mensual = credito?.mensual;
  const diaRenovacion = credito?.dia_renovacion;
  // Los campos toman lo guardado mientras no se estén editando
  useEffect(() => {
    if (editadoRef.current || mensual === undefined || mensual === null) return;
    setMensualTxt(String(mensual).replace('.', ','));
    setDiaTxt(diaRenovacion ? String(diaRenovacion) : '');
  }, [mensual, diaRenovacion]);

  const guardar = async () => {
    setGuardando(true);
    setError(null);
    setGuardadoOk(false);
    const limpio = mensualTxt.trim().replace(',', '.');
    const numero = limpio === '' ? null : Number(limpio);
    const dia = diaTxt.trim() === '' ? null : Number(diaTxt);
    try {
      const res = await ajustesClavesAPI.guardarCreditoAnthropic({
        credito_mensual_usd: Number.isNaN(numero) ? mensualTxt : numero,
        dia_renovacion: Number.isNaN(dia) ? diaTxt : dia,
      });
      editadoRef.current = false;
      setGuardadoOk(true);
      if (onGuardado) onGuardado(res.data?.credito || null);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo guardar el crédito');
    }
    setGuardando(false);
  };

  const pct = credito ? Math.min(100, Math.max(0, Number(credito.porcentaje_usado) || 0)) : 0;
  const conversaciones = Number(credito?.conversaciones) || 0;
  const claseCampo = 'w-full border rounded-md px-3 py-1.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-300';

  return (
    <div className="mt-3 pt-3 border-t" data-testid="credito-claude">
      <p className="text-xs font-semibold text-gray-700 mb-1.5">Crédito del mes</p>
      {!credito ? (
        <p className="text-xs text-gray-400 mb-2">No se pudo calcular el crédito del mes.</p>
      ) : (
        <div className="text-xs text-gray-600 space-y-1 mb-2 tabular-nums">
          <p>
            Gastado {fmtDolares(credito.gastado)} $ · quedan <span className="font-semibold text-gray-800">{fmtDolares(credito.restante)} $</span> de {fmtLimiteDolares(credito.mensual)} $
          </p>
          <div
            className="h-1 w-full overflow-hidden rounded-full bg-gray-100"
            role="progressbar"
            aria-label="Crédito del mes usado (estimado)"
            aria-valuemin={0}
            aria-valuemax={100}
            aria-valuenow={Math.round(pct)}
          >
            <div className={`h-full rounded-full ${colorCredito(pct)}`} style={{ width: `${pct}%` }} />
          </div>
          <p className="text-gray-500">
            Ciclo del {fmtDiaMes(credito.ciclo_desde)} al {fmtDiaMes(credito.ciclo_hasta, 1)} · se renueva el {fmtDiaMes(credito.ciclo_hasta)} · {conversaciones} {conversaciones === 1 ? 'conversación' : 'conversaciones'}
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-end gap-2 mb-2">
        <label className="block flex-1 min-w-0">
          <span className="block text-[11px] text-gray-500 mb-0.5">Crédito mensual ($)</span>
          <input
            type="text"
            inputMode="decimal"
            value={mensualTxt}
            onChange={(e) => { editadoRef.current = true; setGuardadoOk(false); setMensualTxt(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
            className={claseCampo}
            data-testid="input-credito-mensual"
          />
        </label>
        <label className="block sm:w-36">
          <span className="block text-[11px] text-gray-500 mb-0.5">Se renueva el día</span>
          <input
            type="number"
            min={1}
            max={28}
            step={1}
            value={diaTxt}
            onChange={(e) => { editadoRef.current = true; setGuardadoOk(false); setDiaTxt(e.target.value); }}
            onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
            className={claseCampo}
            data-testid="input-credito-dia"
          />
        </label>
        <button
          onClick={guardar}
          disabled={guardando}
          className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-1.5 whitespace-nowrap"
          data-testid="btn-guardar-credito-claude"
        >
          {guardando ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
          {guardando ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
      {error && <p className="text-xs text-red-600 mb-1">{error}</p>}
      {guardadoOk && !error && <p className="text-xs text-green-700 mb-1">Guardado</p>}

      <p className="text-[11px] text-gray-400">
        El ERP lo estima con lo que gasta Ayuda IA. El saldo exacto y la fecha de renovación están en{' '}
        <a
          href="https://platform.claude.com/settings/billing"
          target="_blank"
          rel="noopener noreferrer"
          className="underline hover:text-gray-600"
        >
          platform.claude.com &gt; Settings &gt; Billing &gt; Promotional credits
        </a>.
      </p>
    </div>
  );
};

// Tarjeta "Claude (Ayuda IA)": la clave se escribe en un campo de contraseña
// sin estado de React (ref), se manda al servidor y el campo se vacía. El
// servidor la guarda cifrada y nunca la devuelve.
const TarjetaClaveClaude = ({ onCambio }) => {
  const [estado, setEstado] = useState(null);
  const [cargando, setCargando] = useState(null); // 'guardar' | 'probar' | 'quitar'
  const [error, setError] = useState(null);
  const campoRef = useRef(null);

  const cargar = useCallback(async () => {
    try {
      const res = await ajustesClavesAPI.estadoAnthropic();
      setEstado(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'No se pudo leer el estado de la clave');
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  // La tarjeta aparece al cambiar de sección (sin cambiar de ruta): avisa a la guía
  useEffect(() => {
    if (!estado) return undefined;
    const t = setTimeout(revisarNovedades, 300);
    return () => clearTimeout(t);
  }, [estado]);

  const accion = async (tipo, llamada) => {
    setCargando(tipo);
    setError(null);
    try {
      const res = await llamada();
      setEstado(res.data);
      if (onCambio) onCambio();
    } catch (e) {
      setError(e.response?.data?.detail || 'Error de conexión');
    }
    setCargando(null);
  };

  const guardar = () => {
    const campo = campoRef.current;
    const clave = (campo?.value || '').trim();
    if (campo) campo.value = '';
    if (!clave) { setError('Pega la clave antes de guardar.'); return; }
    accion('guardar', () => ajustesClavesAPI.guardarAnthropic(clave));
  };

  const quitar = () => {
    if (!window.confirm('¿Quitar la clave de Claude guardada en Ajustes? Ayuda IA dejará de funcionar hasta que pongas otra.')) return;
    accion('quitar', () => ajustesClavesAPI.quitarAnthropic());
  };

  const desdeEntorno = estado?.origen === 'entorno';
  const guardadaEnAjustes = estado?.origen === 'ajustes';
  const probada = estado?.probado_en && estado?.prueba_ok !== null && estado?.prueba_ok !== undefined;

  return (
    <div className="bg-white rounded-xl border p-4 shadow-sm" data-guia="ajustes-clave-claude" data-testid="tarjeta-clave-claude">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <KeyRound className="w-4 h-4 text-gray-500 flex-shrink-0" />
          <span className="font-semibold text-sm text-gray-800">Claude (Ayuda IA)</span>
        </div>
        {estado && (
          <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
            estado.configurada ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {estado.configurada ? 'Configurada' : 'Sin configurar'}
          </span>
        )}
      </div>

      {!estado ? (
        <p className="text-xs text-gray-400 mb-2">Cargando…</p>
      ) : (
        <div className="text-xs text-gray-600 space-y-1 mb-3">
          {estado.configurada ? (
            <p>
              Configurada, termina en <span className="font-mono">····{estado.ultimos4}</span>
              {desdeEntorno ? ' · desde el servidor' : ' · desde Ajustes'}
            </p>
          ) : (
            <p>Sin configurar</p>
          )}
          {desdeEntorno && (
            <p className="text-gray-500">
              La clave viene de la configuración del servidor y manda sobre la de Ajustes. Desde aquí solo se puede probar.
            </p>
          )}
          {estado.guardada_ilegible && (
            <p className="text-amber-700">Hay una clave guardada que ya no se puede leer. Vuelve a pegarla.</p>
          )}
          {guardadaEnAjustes && estado.actualizado_en && (
            <p className="text-gray-500">
              Guardada por {estado.actualizado_por_nombre || 'alguien'} el {fmtFechaHora(estado.actualizado_en)}
            </p>
          )}
          {probada && (
            <p className={`flex items-center gap-1.5 ${estado.prueba_ok ? 'text-green-700' : 'text-red-600'}`}>
              {estado.prueba_ok
                ? <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" />
                : <XCircle className="w-3.5 h-3.5 flex-shrink-0" />}
              <span>{estado.prueba_detalle} · {fmtFechaHora(estado.probado_en)}</span>
            </p>
          )}
        </div>
      )}

      {!desdeEntorno && (
        <div className="flex flex-col sm:flex-row gap-2 mb-2">
          <input
            ref={campoRef}
            type="password"
            name="clave-claude"
            autoComplete="off"
            autoCapitalize="none"
            autoCorrect="off"
            spellCheck={false}
            placeholder="Pega aquí la clave (empieza por sk-ant-)"
            onKeyDown={(e) => { if (e.key === 'Enter') guardar(); }}
            className="flex-1 min-w-0 border rounded-md px-3 py-1.5 text-base sm:text-sm focus:outline-none focus:ring-2 focus:ring-gray-300"
            data-testid="input-clave-claude"
          />
          <button
            onClick={guardar}
            disabled={!!cargando}
            className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-40 flex items-center justify-center gap-1.5 whitespace-nowrap"
            data-testid="btn-guardar-clave-claude"
          >
            {cargando === 'guardar' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
            {cargando === 'guardar' ? 'Guardando…' : 'Guardar y probar'}
          </button>
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => accion('probar', () => ajustesClavesAPI.probarAnthropic())}
          disabled={!!cargando || !estado?.configurada}
          className="text-xs px-3 py-1.5 rounded-md border bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-40 flex items-center gap-1.5"
          data-testid="btn-probar-clave-claude"
        >
          {cargando === 'probar' ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
          {cargando === 'probar' ? 'Probando…' : 'Probar conexión'}
        </button>
        {guardadaEnAjustes && (
          <button
            onClick={quitar}
            disabled={!!cargando}
            className="text-xs px-3 py-1.5 rounded-md border border-red-200 bg-red-50 hover:bg-red-100 text-red-700 disabled:opacity-40"
            data-testid="btn-quitar-clave-claude"
          >
            {cargando === 'quitar' ? 'Quitando…' : 'Quitar clave'}
          </button>
        )}
      </div>

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      <p className="text-[11px] text-gray-400 mt-3">
        La clave se guarda cifrada en el servidor y no se vuelve a enseñar. Se crea en platform.claude.com &gt; API Keys, en la organización vinculada al plan Max.
      </p>

      {estado && (
        <BloqueCreditoClaude
          credito={estado.credito || null}
          onGuardado={(credito) => setEstado((prev) => ({ ...(prev || {}), credito }))}
        />
      )}
    </div>
  );
};

const Ajustes = () => {
  const { token, user } = useAuth();
  const [seccion, setSeccion] = useState('monitor');
  const [diagnostico, setDiagnostico] = useState(null);
  const [backupsData, setBackupsData] = useState(null);
  const [loading, setLoading] = useState({});
  const [mensaje, setMensaje] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditFiltro, setAuditFiltro] = useState('');
  const [expandido, setExpandido] = useState({}); // `${nombre}__${coleccion}` -> {loading, items, error}
  const [recursos, setRecursos] = useState(null);
  const [conexiones, setConexiones] = useState(null);
  const [testConn, setTestConn] = useState({}); // clave -> {loading, ok, detalle}
  const [pushTest, setPushTest] = useState(null);
  const [pushLocal, setPushLocal] = useState(null);
  const [emisor, setEmisor] = useState(null); // datos fiscales del emisor (FACe/Facturae)
  // Importador de DIR3 desde facturas .xsig
  const [xsigFiles, setXsigFiles] = useState([]);
  const [analisisFace, setAnalisisFace] = useState(null);
  const [importLoading, setImportLoading] = useState(false);
  const [selDestinos, setSelDestinos] = useState({}); // "gi_di" -> bool

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const fetchDiagnostico = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/diagnostico`, { headers });
      if (res.ok) setDiagnostico(await res.json());
    } catch (e) { console.error(e); }
  }, [token]);

  const fetchBackups = async () => {
    setLoading(l => ({ ...l, backups: true }));
    try {
      const res = await fetch(`${API_URL}/api/sistema/backups?limite=5`, { headers });
      if (res.ok) setBackupsData(await res.json());
    } catch (e) { console.error(e); }
    setLoading(l => ({ ...l, backups: false }));
  };

  const fetchRecursos = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sistema/recursos?dias=30`, { headers });
      if (res.ok) setRecursos(await res.json());
    } catch (e) { console.error(e); }
  };

  const fetchConexiones = async () => {
    try {
      const res = await fetch(`${API_URL}/api/sistema/conexiones`, { headers });
      if (res.ok) setConexiones((await res.json()).conexiones || []);
    } catch (e) { console.error(e); }
  };

  const revisarPushLocal = async () => {
    const soporta = 'serviceWorker' in navigator && 'PushManager' in window;
    if (!soporta) { setPushLocal({ soporta: false }); return; }
    const permiso = Notification.permission;
    let suscrito = false;
    try {
      const reg = await navigator.serviceWorker.getRegistration();
      const sub = reg ? await reg.pushManager.getSubscription() : null;
      suscrito = !!sub;
    } catch (e) { /* ignorar */ }
    setPushLocal({ soporta: true, permiso, suscrito });
  };

  const activarPushLocal = async () => {
    const permiso = await requestNotificationPermission();
    if (permiso === 'granted' && user) {
      await subscribeToPush(user.empleado_id, user.id, user.rol || user.role);
    }
    await revisarPushLocal();
  };

  const probarPush = async () => {
    setPushTest({ loading: true });
    try {
      const res = await fetch(`${API_URL}/api/push/probar`, { method: 'POST', headers });
      const data = await res.json();
      setPushTest(res.ok ? { data } : { error: data.detail || 'Error al enviar la prueba' });
    } catch (e) {
      setPushTest({ error: 'Error de conexión' });
    }
  };

  const probarConexion = async (clave) => {
    setTestConn(t => ({ ...t, [clave]: { loading: true } }));
    try {
      const res = await fetch(`${API_URL}/api/sistema/conexiones/${clave}/test`, { method: 'POST', headers });
      const data = await res.json();
      setTestConn(t => ({ ...t, [clave]: { ok: data.ok, detalle: data.detalle } }));
    } catch (e) {
      setTestConn(t => ({ ...t, [clave]: { ok: false, detalle: 'Error de conexión' } }));
    }
  };

  const fetchEmisor = async () => {
    try {
      const res = await fetch(`${API_URL}/api/face/emisor`, { headers });
      if (res.ok) setEmisor(await res.json());
    } catch (e) { console.error(e); }
  };

  const guardarEmisor = async () => {
    setLoading(l => ({ ...l, emisor: true }));
    try {
      const res = await fetch(`${API_URL}/api/face/emisor`, { method: 'PUT', headers, body: JSON.stringify(emisor || {}) });
      if (res.ok) {
        setEmisor(await res.json());
        setMensaje({ tipo: 'ok', texto: 'Datos de facturación guardados correctamente' });
      } else {
        setMensaje({ tipo: 'error', texto: 'No se pudieron guardar los datos de facturación' });
      }
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión al guardar' });
    }
    setLoading(l => ({ ...l, emisor: false }));
  };

  const setEmisorField = (campo, valor) => setEmisor(e => ({ ...(e || {}), [campo]: valor }));

  // --- Importador DIR3 desde .xsig ---
  const analizarXsig = async () => {
    if (!xsigFiles.length) return;
    setImportLoading(true);
    setAnalisisFace(null);
    try {
      const fd = new FormData();
      for (const f of xsigFiles) fd.append('files', f);
      // Sin Content-Type: el navegador pone el boundary del multipart
      const res = await fetch(`${API_URL}/api/face/importar-dir3/analizar`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const data = await res.json();
        setAnalisisFace(data);
        const sel = {};
        data.grupos.forEach((g, gi) => {
          if (g.cliente) g.destinos.forEach((d, di) => { if (!d.ya_existe) sel[`${gi}_${di}`] = true; });
        });
        setSelDestinos(sel);
      } else {
        setMensaje({ tipo: 'error', texto: 'No se pudieron analizar los ficheros' });
      }
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión al analizar' });
    }
    setImportLoading(false);
  };

  const aplicarImportFace = async () => {
    const asigMap = {};
    analisisFace.grupos.forEach((g, gi) => {
      if (!g.cliente) return;
      g.destinos.forEach((d, di) => {
        if (selDestinos[`${gi}_${di}`]) {
          (asigMap[g.cliente.id] = asigMap[g.cliente.id] || []).push({
            nombre: d.nombre,
            oficina_contable: d.oficina_contable,
            organo_gestor: d.organo_gestor,
            unidad_tramitadora: d.unidad_tramitadora,
            organo_proponente: d.organo_proponente,
          });
        }
      });
    });
    const asignaciones = Object.entries(asigMap).map(([cliente_id, destinos]) => ({ cliente_id, destinos }));
    if (!asignaciones.length) {
      setMensaje({ tipo: 'error', texto: 'Selecciona al menos un destino para importar' });
      return;
    }
    setImportLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/face/importar-dir3/aplicar`, {
        method: 'POST', headers, body: JSON.stringify({ asignaciones }),
      });
      if (res.ok) {
        const data = await res.json();
        setMensaje({ tipo: 'ok', texto: `Importados ${data.destinos_añadidos} destino(s) DIR3 en ${data.clientes_actualizados} cliente(s)` });
        setAnalisisFace(null); setXsigFiles([]); setSelDestinos({});
      } else {
        setMensaje({ tipo: 'error', texto: 'No se pudieron aplicar los destinos' });
      }
    } catch (e) {
      setMensaje({ tipo: 'error', texto: 'Error de conexión al aplicar' });
    }
    setImportLoading(false);
  };

  const fetchAuditLog = async (coleccion = '') => {
    try {
      const params = new URLSearchParams({ limite: '100' });
      if (coleccion) params.append('coleccion', coleccion);
      const res = await fetch(`${API_URL}/api/audit-log?${params}`, { headers });
      if (res.ok) setAuditLogs(await res.json());
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    fetchDiagnostico();
    fetchRecursos();
    const interval = setInterval(fetchDiagnostico, 15000);
    return () => clearInterval(interval);
  }, [fetchDiagnostico]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (seccion === 'backups') fetchBackups();
    if (seccion === 'auditoria') fetchAuditLog(auditFiltro);
    if (seccion === 'conexiones') { fetchConexiones(); revisarPushLocal(); }
    if (seccion === 'facturacion') fetchEmisor();
  }, [seccion]); // eslint-disable-line react-hooks/exhaustive-deps

  // Descarga COMPLETA y fiel (BSON, todas las colecciones) por enlace directo.
  const descargarBDCompleta = () => {
    setMensaje({ tipo: 'ok', texto: 'Preparando descarga completa… puede tardar según el tamaño de la BD.' });
    const url = `${API_URL}/api/migracion/exportar-bd-completa?token=${encodeURIComponent(token)}`;
    window.location.href = url;
  };

  const toggleCambio = async (backup, coleccion) => {
    const key = `${backup.nombre}__${coleccion}`;
    if (expandido[key]) {
      setExpandido(e => { const n = { ...e }; delete n[key]; return n; });
      return;
    }
    if (!backup.generated_at || !backup.prev_generated_at) {
      setExpandido(e => ({ ...e, [key]: { error: 'No hay ventana de comparación disponible.' } }));
      return;
    }
    setExpandido(e => ({ ...e, [key]: { loading: true } }));
    try {
      const params = new URLSearchParams({
        coleccion,
        desde: backup.prev_generated_at,
        hasta: backup.generated_at,
        limite: '100',
      });
      const res = await fetch(`${API_URL}/api/sistema/backups/cambios?${params}`, { headers });
      const data = await res.json();
      if (res.ok) {
        setExpandido(e => ({ ...e, [key]: { items: data.items || [] } }));
      } else {
        setExpandido(e => ({ ...e, [key]: { error: data.detail || 'Error cargando detalle' } }));
      }
    } catch (err) {
      setExpandido(e => ({ ...e, [key]: { error: 'Error de conexión' } }));
    }
  };

  const memUsage = diagnostico?.memoria_mb || 0;
  const memLimit = diagnostico?.memoria_limite_mb || 0;
  const cpuCount = diagnostico?.cpu_count;
  // Uso del HOST (servidor entero) — lo relevante para dimensionar
  const hostMemPct = diagnostico?.host_mem_pct ?? null;
  const hostMemUsed = diagnostico?.host_mem_used_mb ?? 0;
  const hostMemTotal = diagnostico?.host_mem_total_mb ?? memLimit;
  const diskPct = diagnostico?.disk_pct ?? null;
  const diskUsed = diagnostico?.disk_used_gb ?? 0;
  const diskTotal = diagnostico?.disk_total_gb ?? 0;
  const rec = recursos?.recomendacion;
  const resumen = recursos?.resumen;

  const secciones = [
    { id: 'monitor', label: 'Monitor', icon: Activity },
    { id: 'conexiones', label: 'Conexiones', icon: Plug },
    { id: 'backups', label: 'Backups', icon: HardDrive },
    { id: 'auditoria', label: 'Auditoría', icon: FileText },
    { id: 'facturacion', label: 'Facturación', icon: Building2 },
  ];

  const getMemColor = (pct) => {
    if (pct < 60) return 'bg-green-500';
    if (pct < 75) return 'bg-yellow-500';
    if (pct < 90) return 'bg-orange-500';
    return 'bg-red-500';
  };

  const fmtGB = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb.toFixed(0)} MB`);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="ajustes-page">
      <div className="max-w-6xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-2.5 bg-gradient-to-br from-gray-700 to-gray-900 rounded-xl">
            <Settings className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ajustes</h1>
            <p className="text-sm text-gray-500">Monitor del servidor, backups y auditoría</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-white rounded-xl p-1 shadow-sm border mb-6 w-fit">
          {secciones.map(s => {
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setSeccion(s.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  seccion === s.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-gray-100'
                }`}
                data-testid={`tab-${s.id}`}
              >
                <Icon className="w-4 h-4" />
                {s.label}
              </button>
            );
          })}
        </div>

        {/* Mensaje */}
        {mensaje && (
          <div className={`mb-4 p-3 rounded-lg flex items-center gap-2 text-sm ${
            mensaje.tipo === 'ok' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'
          }`}>
            {mensaje.tipo === 'ok' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {mensaje.texto}
            <button onClick={() => setMensaje(null)} className="ml-auto text-xs underline">Cerrar</button>
          </div>
        )}

        {/* ===== MONITOR ===== */}
        {seccion === 'monitor' && (
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {/* RAM del host */}
              <div className="bg-white rounded-xl border p-5 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2">
                    <MemoryStick className="w-5 h-5 text-blue-600" />
                    <span className="font-semibold text-gray-800">RAM del servidor</span>
                  </div>
                  <button onClick={() => { fetchDiagnostico(); fetchRecursos(); }} className="p-1 hover:bg-gray-100 rounded" data-testid="refresh-diagnostico">
                    <RefreshCw className="w-4 h-4 text-gray-400" />
                  </button>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{hostMemPct != null ? `${hostMemPct.toFixed(0)}%` : '—'}</div>
                <div className="text-sm text-gray-500 mb-3">{fmtGB(hostMemUsed)} de {fmtGB(hostMemTotal)}</div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full transition-all duration-500 ${getMemColor(hostMemPct || 0)}`} style={{ width: `${Math.min(hostMemPct || 0, 100)}%` }} />
                </div>
                <div className="mt-2 text-xs text-gray-400">Backend: {memUsage.toFixed(0)} MB de {fmtGB(memLimit)}</div>
              </div>

              {/* CPU */}
              <div className="bg-white rounded-xl border p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Cpu className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-gray-800">CPU</span>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">
                  {recursos?.actual?.cpu_pct != null ? `${recursos.actual.cpu_pct.toFixed(0)}%` : `${cpuCount ?? '—'} vCPU`}
                </div>
                <div className="text-sm text-gray-500 mb-3">
                  {cpuCount ? `${cpuCount} vCPU` : ''}{recursos?.actual?.load1 != null ? ` · carga ${recursos.actual.load1}` : ''}
                </div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full rounded-full bg-purple-500" style={{ width: `${Math.min(recursos?.actual?.cpu_pct || 0, 100)}%` }} />
                </div>
                <div className="mt-2 text-xs text-gray-400">Uso medio de las {cpuCount || '—'} vCPU</div>
              </div>

              {/* Disco */}
              <div className="bg-white rounded-xl border p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <HardDriveDownload className="w-5 h-5 text-teal-600" />
                  <span className="font-semibold text-gray-800">Disco</span>
                </div>
                <div className="text-3xl font-bold text-gray-900 mb-1">{diskPct != null ? `${diskPct.toFixed(0)}%` : '—'}</div>
                <div className="text-sm text-gray-500 mb-3">{diskUsed.toFixed(1)} de {diskTotal.toFixed(0)} GB</div>
                <div className="w-full h-3 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${getMemColor(diskPct || 0)}`} style={{ width: `${Math.min(diskPct || 0, 100)}%` }} />
                </div>
                <div className="mt-2 text-xs text-gray-400">Almacenamiento del VPS</div>
              </div>

              {/* Estado */}
              <div className="bg-white rounded-xl border p-5 shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <Server className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-gray-800">Estado</span>
                </div>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`w-3 h-3 rounded-full ${diagnostico?.critico ? 'bg-red-500 animate-pulse' : diagnostico?.alerta ? 'bg-yellow-500 animate-pulse' : 'bg-green-500'}`} />
                  <span className="text-lg font-bold text-gray-900">
                    {diagnostico?.critico ? 'Crítico' : diagnostico?.alerta ? 'Alerta' : 'Saludable'}
                  </span>
                </div>
                <div className="text-xs text-gray-400">VPS Hetzner · Docker</div>
                {diagnostico?.timestamp && (
                  <div className="text-xs text-gray-400 mt-1">
                    {new Date(diagnostico.timestamp).toLocaleTimeString('es-ES')}
                  </div>
                )}
              </div>
            </div>

            {/* Recomendación de dimensionado */}
            {rec && resumen && (
              <div className={`rounded-xl border p-4 ${
                rec.estado === 'sobredimensionado' ? 'bg-green-50 border-green-200'
                : rec.estado === 'ajustado' ? 'bg-amber-50 border-amber-200'
                : 'bg-blue-50 border-blue-200'
              }`}>
                <div className="flex items-start gap-3">
                  {rec.estado === 'sobredimensionado' ? <TrendingDown className="w-5 h-5 mt-0.5 text-green-600" />
                    : rec.estado === 'ajustado' ? <TrendingUp className="w-5 h-5 mt-0.5 text-amber-600" />
                    : <Shield className="w-5 h-5 mt-0.5 text-blue-600" />}
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-800 text-sm">Dimensionado del servidor</h4>
                    <p className="text-sm text-gray-700 mt-0.5">{rec.mensaje}</p>
                    <p className="text-xs text-gray-500 mt-1.5">
                      Últimos {recursos.dias} días · pico RAM {resumen.peak_mem_pct}% (media {resumen.avg_mem_pct}%) ·
                      pico CPU {resumen.peak_cpu_pct}% (media {resumen.avg_cpu_pct}%)
                      {rec.tier_actual && ` · actual: ${rec.tier_actual.nombre} (~${rec.tier_actual.eur_mes} €/mes)`}
                    </p>
                    <p className="text-[11px] text-gray-400 mt-1">Tarifas Hetzner orientativas; el pico manda para no quedarte corto.</p>
                  </div>
                </div>
              </div>
            )}
            {recursos && !recursos.suficientes_datos && (
              <div className="rounded-xl border bg-gray-50 border-gray-200 p-4 text-sm text-gray-600">
                Recopilando datos de uso (se muestrea cada 10 min). La recomendación de dimensionado aparecerá en cuanto haya histórico suficiente.
              </div>
            )}

            {/* Gráfica de evolución */}
            {recursos?.serie?.length > 1 && (
              <div className="bg-white rounded-xl border p-5 shadow-sm">
                <h3 className="font-semibold text-gray-800 mb-3">Uso del servidor · últimos {recursos.dias} días</h3>
                <div className="h-64">
                  <Line
                    data={{
                      labels: recursos.serie.map(d => d.fecha.slice(5)),
                      datasets: [
                        { label: 'RAM máx %', data: recursos.serie.map(d => d.mem_max), borderColor: '#2563eb', backgroundColor: 'rgba(37,99,235,0.12)', fill: true, tension: 0.3 },
                        { label: 'RAM media %', data: recursos.serie.map(d => d.mem_avg), borderColor: '#93c5fd', fill: false, tension: 0.3 },
                        { label: 'CPU máx %', data: recursos.serie.map(d => d.cpu_max), borderColor: '#7c3aed', fill: false, tension: 0.3 },
                      ],
                    }}
                    options={{
                      responsive: true, maintainAspectRatio: false,
                      interaction: { mode: 'index', intersect: false },
                      plugins: { legend: { position: 'top' } },
                      scales: { y: { beginAtZero: true, max: 100, title: { display: true, text: '% uso' } } },
                    }}
                  />
                </div>
              </div>
            )}

            {/* Estado de la conexión Gemini (si se prueba) */}
            {diagnostico?.gemini_test?.intentado && (
              <div className={`rounded-xl border p-4 ${diagnostico.gemini_test.ok ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                <div className="flex items-start gap-3">
                  <Shield className={`w-5 h-5 mt-0.5 ${diagnostico.gemini_test.ok ? 'text-green-600' : 'text-amber-600'}`} />
                  <div>
                    <h4 className="font-semibold text-gray-800 text-sm">Conexión IA (Gemini)</h4>
                    <p className="text-sm text-gray-600 mt-0.5">
                      {diagnostico.gemini_test.ok ? 'Conexión correcta con Gemini.' : `Problema de conexión: ${diagnostico.gemini_test.error}`}
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ===== CONEXIONES ===== */}
        {seccion === 'conexiones' && (
          <div className="space-y-5" data-testid="seccion-conexiones">
            <div className="flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Estado de las integraciones externas. "Probar" hace una llamada real a cada servicio.
              </p>
              <button onClick={fetchConexiones} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                <RefreshCw className="w-4 h-4" />
              </button>
            </div>

            <TarjetaClaveClaude onCambio={fetchConexiones} />

            <div className="bg-white rounded-xl border p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-4 h-4 text-gray-500" />
                <span className="font-semibold text-sm text-gray-800">Notificaciones push</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                Abre esta pantalla en el dispositivo que quieras comprobar. "Enviar prueba" manda una notificación a todos tus dispositivos suscritos.
              </p>
              {pushLocal && (
                <p className="text-xs mb-2">
                  Este dispositivo:{' '}
                  {!pushLocal.soporta ? (
                    <span className="text-gray-500">no soporta notificaciones push</span>
                  ) : pushLocal.permiso === 'denied' ? (
                    <span className="text-red-600">permiso bloqueado en el navegador</span>
                  ) : pushLocal.suscrito ? (
                    <span className="text-green-700">suscrito</span>
                  ) : (
                    <span className="text-amber-600">sin suscripción activa</span>
                  )}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap">
                {pushLocal?.soporta && !pushLocal.suscrito && pushLocal.permiso !== 'denied' && (
                  <button onClick={activarPushLocal}
                    className="text-xs px-3 py-1.5 rounded-md border bg-gray-50 hover:bg-gray-100 text-gray-700">
                    Activar en este dispositivo
                  </button>
                )}
                <button onClick={probarPush} disabled={pushTest?.loading}
                  className="text-xs px-3 py-1.5 rounded-md border bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-40 flex items-center gap-1.5">
                  {pushTest?.loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Bell className="w-3 h-3" />}
                  {pushTest?.loading ? 'Enviando…' : 'Enviar prueba a mis dispositivos'}
                </button>
              </div>
              {pushTest?.error && <p className="text-xs text-red-600 mt-2">{pushTest.error}</p>}
              {pushTest?.data && (
                <div className="mt-2 text-xs text-gray-600 space-y-1">
                  <p>
                    {pushTest.data.total === 0
                      ? (pushTest.data.mensaje || 'No hay dispositivos suscritos')
                      : `${pushTest.data.enviadas} enviada(s), ${pushTest.data.caducadas} caducada(s) de ${pushTest.data.total} dispositivo(s)`}
                  </p>
                  {(pushTest.data.detalle || []).map((d, i) => (
                    <p key={`push-detalle-${i}`} className="text-gray-500">
                      {d.dispositivo} · {d.resultado}{d.registrada ? ` · registrada ${new Date(d.registrada).toLocaleDateString('es-ES')}` : ''}
                    </p>
                  ))}
                </div>
              )}
            </div>

            {!conexiones ? (
              <div className="text-center py-10 text-gray-400 text-sm">Cargando…</div>
            ) : (
              [...new Set(conexiones.map(c => c.categoria))].map(cat => (
                <div key={cat}>
                  <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">{cat}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {conexiones.filter(c => c.categoria === cat).map(c => {
                      const t = testConn[c.clave];
                      return (
                        <div key={c.clave} className="bg-white rounded-xl border p-4 shadow-sm">
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Wifi className={`w-4 h-4 flex-shrink-0 ${c.configurado ? 'text-gray-500' : 'text-gray-300'}`} />
                              <span className="font-semibold text-sm text-gray-800 truncate">{c.nombre}</span>
                            </div>
                            <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                              c.configurado ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                            }`}>
                              {c.configurado ? 'Configurada' : 'Sin configurar'}
                            </span>
                          </div>

                          {c.ultima_sync && (
                            <p className="text-xs text-gray-500 mb-2">
                              Última sync: {new Date(c.ultima_sync).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' })}
                            </p>
                          )}

                          <div className="flex items-center gap-2 mt-2">
                            {c.tiene_test ? (
                              <button
                                onClick={() => probarConexion(c.clave)}
                                disabled={!c.configurado || t?.loading}
                                className="text-xs px-3 py-1.5 rounded-md border bg-gray-50 hover:bg-gray-100 text-gray-700 disabled:opacity-40 flex items-center gap-1.5"
                              >
                                {t?.loading ? <RefreshCw className="w-3 h-3 animate-spin" /> : <Plug className="w-3 h-3" />}
                                {t?.loading ? 'Probando…' : 'Probar'}
                              </button>
                            ) : (
                              <span className="text-[11px] text-gray-400">Sin prueba en vivo</span>
                            )}
                            {t && !t.loading && (
                              t.ok
                                ? <CheckCircle className="w-4 h-4 text-green-600 flex-shrink-0" />
                                : <XCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
                            )}
                          </div>
                          {t && !t.loading && t.detalle && (
                            <p className={`text-xs mt-1.5 ${t.ok ? 'text-green-700' : 'text-red-600'}`}>{t.detalle}</p>
                          )}
                          {!t && c.prueba_detalle && (
                            <p className={`text-xs mt-1.5 ${c.prueba_ok ? 'text-green-700' : 'text-red-600'}`}>
                              Última prueba: {c.prueba_detalle}{c.probado_en ? ` · ${fmtFechaHora(c.probado_en)}` : ''}
                            </p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* ===== BACKUPS ===== */}
        {seccion === 'backups' && (
          <div className="space-y-4">
            {/* Info copia automática */}
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
              <strong>Copia de seguridad automática diaria.</strong> Cada día a las <strong>03:30</strong> (hora del servidor)
              se hace un <code>mongodump</code> completo de toda la base de datos (formato BSON), con <strong>rotación de 30 días</strong>,
              almacenado en el propio VPS (<code>/opt/duetto/backups</code>).
            </div>

            {/* Descarga completa */}
            <div className="bg-white rounded-xl border p-5 shadow-sm">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <h3 className="font-semibold text-gray-800">Descargar base de datos completa</h3>
                  <p className="text-sm text-gray-500 mt-1 max-w-xl">
                    Descarga ahora una copia fiel de <strong>todas</strong> las colecciones (BSON, conserva IDs y tipos).
                    Se restaura con <code>mongorestore</code>.
                  </p>
                </div>
                <button onClick={descargarBDCompleta}
                  className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 whitespace-nowrap"
                  data-testid="descargar-bd-completa-btn">
                  <Download className="w-4 h-4" /> Descargar BD completa
                </button>
              </div>
            </div>

            {/* Lista de copias del VPS */}
            <div className="bg-white rounded-xl border shadow-sm">
              <div className="p-4 border-b flex items-center justify-between">
                <h3 className="font-semibold text-gray-800">
                  Últimas copias {backupsData?.total_copias ? `(${backupsData.total_copias} guardadas)` : ''}
                </h3>
                <button onClick={fetchBackups} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg">
                  <RefreshCw className={`w-4 h-4 ${loading.backups ? 'animate-spin' : ''}`} />
                </button>
              </div>

              {backupsData && !backupsData.configurado ? (
                <div className="p-6 text-sm text-amber-700 bg-amber-50">
                  {backupsData.mensaje || 'El directorio de backups no está accesible.'}
                </div>
              ) : !backupsData?.backups?.length ? (
                <div className="p-8 text-center text-gray-400">
                  <HardDrive className="w-10 h-10 mx-auto mb-2" />
                  <p className="text-sm">{loading.backups ? 'Cargando…' : 'No hay copias todavía'}</p>
                </div>
              ) : (
                <div className="divide-y">
                  {backupsData.backups.map((b) => (
                    <div key={b.nombre} className="p-4">
                      <div className="flex items-start gap-3">
                        <HardDrive className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-gray-800">
                              {b.fecha ? new Date(b.fecha).toLocaleString('es-ES', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : b.nombre}
                            </p>
                            <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{b.tamano}</span>
                            {b.total_docs != null && (
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{b.total_docs.toLocaleString('es-ES')} docs</span>
                            )}
                          </div>

                          {/* Resumen de cambios */}
                          <div className="mt-2">
                            {!b.tiene_manifiesto ? (
                              <p className="text-xs text-gray-400">Copia anterior a la mejora · sin resumen de cambios</p>
                            ) : b.sin_comparacion ? (
                              <p className="text-xs text-gray-400">Sin copia anterior con datos para comparar</p>
                            ) : b.cambios.length === 0 ? (
                              <p className="text-xs text-gray-400">Sin cambios respecto a la copia anterior</p>
                            ) : (
                              <div className="space-y-1">
                                <p className="text-xs font-medium text-gray-600">{b.cambios.length} colección(es) con cambios:</p>
                                <div className="flex flex-wrap gap-1.5">
                                  {b.cambios.map((c) => {
                                    const key = `${b.nombre}__${c.coleccion}`;
                                    const exp = expandido[key];
                                    const up = c.delta > 0;
                                    return (
                                      <button
                                        key={c.coleccion}
                                        onClick={() => toggleCambio(b, c.coleccion)}
                                        className={`flex items-center gap-1 text-xs px-2 py-1 rounded-md border transition-colors ${
                                          exp ? 'bg-gray-900 text-white border-gray-900' : 'bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200'
                                        }`}
                                        title="Ver qué documentos cambiaron"
                                      >
                                        {exp ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                                        <span className="capitalize">{c.coleccion.replace(/_/g, ' ')}</span>
                                        <span className={`font-semibold ${exp ? '' : up ? 'text-green-600' : 'text-red-600'}`}>
                                          {up ? '+' : ''}{c.delta}
                                        </span>
                                      </button>
                                    );
                                  })}
                                </div>

                                {/* Detalle desplegado de cada colección */}
                                {b.cambios.map((c) => {
                                  const key = `${b.nombre}__${c.coleccion}`;
                                  const exp = expandido[key];
                                  if (!exp) return null;
                                  return (
                                    <div key={`det-${key}`} className="mt-2 ml-1 border-l-2 border-gray-200 pl-3">
                                      <p className="text-xs font-medium text-gray-700 mb-1 capitalize">
                                        {c.coleccion.replace(/_/g, ' ')}: {c.antes} → {c.despues}
                                      </p>
                                      {exp.loading ? (
                                        <p className="text-xs text-gray-400">Cargando detalle…</p>
                                      ) : exp.error ? (
                                        <p className="text-xs text-red-500">{exp.error}</p>
                                      ) : !exp.items?.length ? (
                                        <p className="text-xs text-gray-400">No se pudo identificar qué documentos concretos cambiaron (puede que se hayan eliminado o que no registren fecha de modificación).</p>
                                      ) : (
                                        <div className="space-y-1 max-h-48 overflow-y-auto">
                                          {exp.items.map((it) => (
                                            <div key={it.id} className="flex items-center gap-2 text-xs">
                                              <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${it.tipo === 'nuevo' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                                                {it.tipo}
                                              </span>
                                              <span className="text-gray-700 truncate">{it.nombre}</span>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Restaurar */}
            <div className="bg-white rounded-xl border p-5 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-gray-800">Restaurar desde Backup</h3>
                  <p className="text-sm text-gray-500 mt-1">Compara un backup con la base de datos y restaura solo lo que falta o cambió</p>
                </div>
                <a href="/restaurar-backup"
                  className="flex items-center gap-2 px-4 py-2.5 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600"
                  data-testid="btn-ir-restaurar">
                  <Upload className="w-4 h-4" /> Restaurar Backup
                </a>
              </div>
            </div>
          </div>
        )}

        {/* ===== AUDITORÍA ===== */}
        {seccion === 'auditoria' && (
          <div className="space-y-4" data-testid="seccion-auditoria">
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-bold text-gray-900">Log de Auditoría</h3>
                  <p className="text-sm text-gray-500">Registro de eliminaciones, bajas y operaciones críticas</p>
                </div>
                <button onClick={() => fetchAuditLog(auditFiltro)} className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg" data-testid="btn-refresh-audit">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>

              {/* Filtros */}
              <div className="flex gap-2 mb-4 flex-wrap">
                {[
                  { value: '', label: 'Todas' },
                  { value: 'clientes', label: 'Clientes' },
                  { value: 'presupuestos', label: 'Presupuestos' },
                  { value: 'platos', label: 'Platos' },
                  { value: 'menus', label: 'Menús' },
                  { value: 'ingredientes', label: 'Ingredientes' },
                  { value: 'materiales', label: 'Materiales' },
                  { value: 'proveedores', label: 'Proveedores' },
                  { value: 'empleados', label: 'Empleados' },
                  { value: 'facturas', label: 'Facturas' },
                  { value: 'TODAS', label: 'Migraciones' },
                ].map((f) => (
                  <button
                    key={f.value}
                    onClick={() => { setAuditFiltro(f.value); fetchAuditLog(f.value); }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      auditFiltro === f.value ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`audit-filter-${f.value || 'all'}`}
                  >
                    {f.label}
                  </button>
                ))}
              </div>

              {/* Lista de logs */}
              {auditLogs.length === 0 ? (
                <div className="text-center py-10 text-gray-400">
                  <Shield className="w-12 h-12 mx-auto mb-3 opacity-40" />
                  <p className="font-medium">Sin registros de auditoría</p>
                  <p className="text-sm">Las acciones críticas aparecerán aquí</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[500px] overflow-y-auto">
                  {auditLogs.map((log) => {
                    const isDelete = log.accion === 'eliminar';
                    const isRestore = log.accion === 'restaurar_selectivo';
                    const isMigration = log.accion === 'migracion_importar';
                    const isBaja = log.accion === 'baja';

                    const bgColor = isDelete ? 'border-l-red-400 bg-red-50/30' : isRestore ? 'border-l-green-400 bg-green-50/30' : isBaja ? 'border-l-orange-400 bg-orange-50/30' : 'border-l-amber-400 bg-amber-50/30';
                    const badgeColor = isDelete ? 'bg-red-100 text-red-700' : isRestore ? 'bg-green-100 text-green-700' : isBaja ? 'bg-orange-100 text-orange-700' : 'bg-amber-100 text-amber-700';
                    const badgeText = isDelete ? 'Eliminado' : isRestore ? 'Restaurado' : isMigration ? 'Migración' : isBaja ? 'Baja' : log.accion;

                    const fecha = log.fecha ? new Date(log.fecha) : null;
                    const fechaStr = fecha ? fecha.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '';
                    const horaStr = fecha ? fecha.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '';

                    return (
                      <div key={log.id} className={`border border-l-4 rounded-lg p-3 ${bgColor}`} data-testid={`audit-entry-${log.id}`}>
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeColor}`}>{badgeText}</span>
                              <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{log.coleccion}</span>
                            </div>
                            <p className="font-medium text-gray-900 mt-1.5 truncate">{log.entidad_nombre || 'Sin nombre'}</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <div className="flex items-center gap-1 text-xs text-gray-500">
                              <User className="w-3 h-3" />
                              <span>{log.usuario_nombre || 'Sistema'}</span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-gray-400 mt-0.5">
                              <Calendar className="w-3 h-3" />
                              <span>{fechaStr} {horaStr}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {seccion === 'facturacion' && (
          <div className="space-y-4" data-testid="seccion-facturacion">
            <div className="bg-white rounded-xl border p-5">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-bold text-gray-900">Datos fiscales del emisor</h3>
                <button
                  onClick={guardarEmisor}
                  disabled={loading.emisor}
                  className="flex items-center gap-2 px-4 py-2 bg-duetto-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  data-testid="btn-guardar-emisor"
                >
                  <Save className="w-4 h-4" />
                  {loading.emisor ? 'Guardando…' : 'Guardar'}
                </button>
              </div>
              <p className="text-sm text-gray-500 mb-5">
                Datos de Duetto Catering como empresa emisora. Se usan en las facturas electrónicas
                a administraciones públicas (Facturae / FACe).
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { campo: 'razon_social', label: 'Razón social', ph: 'Duetto Catering S.L.', full: true },
                  { campo: 'nombre_comercial', label: 'Nombre comercial', ph: 'Duetto Catering' },
                  { campo: 'cif', label: 'CIF / NIF', ph: 'B12345678' },
                  { campo: 'direccion', label: 'Domicilio fiscal', ph: 'C/ Ejemplo, 123', full: true },
                  { campo: 'codigo_postal', label: 'Código postal', ph: '28001' },
                  { campo: 'ciudad', label: 'Ciudad', ph: 'Madrid' },
                  { campo: 'provincia', label: 'Provincia', ph: 'Madrid' },
                  { campo: 'pais', label: 'País (ISO 3 letras)', ph: 'ESP' },
                  { campo: 'email', label: 'Email', ph: 'info@duettocatering.com' },
                  { campo: 'telefono', label: 'Teléfono', ph: '+34 911 23 45 67' },
                  { campo: 'web', label: 'Web', ph: 'www.duettocatering.com' },
                  { campo: 'iban', label: 'IBAN (cuenta de cobro)', ph: 'ES00 0000 0000 0000 0000 0000', full: true },
                  { campo: 'face_servicekey', label: 'FACe · Token API (alta portal proveedores)', ph: 'token de la API REST de FACe', full: true },
                  { campo: 'face_api_url', label: 'FACe · URL API (vacío = producción; pon la de staging para pruebas)', ph: 'https://api.face.gob.es', full: true },
                ].map((f) => (
                  <div key={f.campo} className={f.full ? 'md:col-span-2' : ''}>
                    <label className="block text-xs font-medium text-gray-600 mb-1">{f.label}</label>
                    <input
                      type="text"
                      value={(emisor && emisor[f.campo]) || ''}
                      onChange={(ev) => setEmisorField(f.campo, ev.target.value)}
                      placeholder={f.ph}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-duetto-primary focus:border-transparent"
                      data-testid={`emisor-${f.campo}`}
                    />
                  </div>
                ))}
              </div>

              <div className="mt-5 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-700 flex gap-2">
                <FileText className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>
                  Estos datos identifican a Duetto como emisora en el XML Facturae. Asegúrate de que
                  el CIF y la razón social coinciden exactamente con los registrados en Hacienda, o FACe
                  podría rechazar la factura.
                </span>
              </div>
            </div>

            {/* Importador de destinos DIR3 desde .xsig */}
            <div className="bg-white rounded-xl border p-5">
              <h3 className="font-bold text-gray-900 mb-1">Importar destinos DIR3 desde facturas (.xsig)</h3>
              <p className="text-sm text-gray-500 mb-4">
                Sube facturas ya emitidas con Factura-e. Se extraen los códigos DIR3 y se asignan a tus
                clientes casando por CIF. Un mismo cliente puede tener varios destinos (concejalías).
              </p>

              <div className="flex flex-wrap items-center gap-3">
                <label className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 cursor-pointer hover:bg-gray-50">
                  <Upload className="w-4 h-4" />
                  Elegir ficheros .xsig
                  <input
                    type="file"
                    multiple
                    accept=".xsig,.xml"
                    className="hidden"
                    onChange={(e) => { setXsigFiles(Array.from(e.target.files || [])); setAnalisisFace(null); }}
                    data-testid="face-xsig-input"
                  />
                </label>
                <span className="text-sm text-gray-500">{xsigFiles.length ? `${xsigFiles.length} fichero(s)` : 'Ninguno seleccionado'}</span>
                <button
                  onClick={analizarXsig}
                  disabled={!xsigFiles.length || importLoading}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                  data-testid="face-analizar"
                >
                  {importLoading && !analisisFace ? 'Analizando…' : 'Analizar'}
                </button>
              </div>

              {analisisFace && (
                <div className="mt-5">
                  <div className="text-sm text-gray-600 mb-3">
                    {analisisFace.ficheros_validos} factura(s) · <b>{analisisFace.clientes_encontrados}</b> cliente(s) encontrados ·{' '}
                    {analisisFace.clientes_no_encontrados} sin coincidencia en la BD
                  </div>
                  <div className="space-y-2 max-h-[420px] overflow-y-auto">
                    {analisisFace.grupos.map((g, gi) => (
                      <div key={gi} className={`border rounded-lg p-3 ${g.cliente ? 'border-gray-200' : 'border-amber-200 bg-amber-50/40'}`}>
                        <div className="flex items-center justify-between mb-2">
                          <div>
                            <span className="font-semibold text-gray-800 text-sm">{g.cliente ? g.cliente.nombre : g.nombre_factura}</span>
                            <span className="ml-2 text-xs text-gray-400">CIF {g.cif}</span>
                          </div>
                          {!g.cliente && <span className="text-xs text-amber-600 font-medium">No está en la BD</span>}
                        </div>
                        <div className="space-y-1">
                          {g.destinos.map((d, di) => {
                            const key = `${gi}_${di}`;
                            return (
                              <label key={di} className={`flex items-center gap-2 text-xs ${(!g.cliente || d.ya_existe) ? 'opacity-50' : ''}`}>
                                <input
                                  type="checkbox"
                                  disabled={!g.cliente || d.ya_existe}
                                  checked={!!selDestinos[key]}
                                  onChange={() => setSelDestinos(s => ({ ...s, [key]: !s[key] }))}
                                />
                                <span className="font-medium text-gray-700">{d.nombre || '(sin nombre)'}</span>
                                <span className="text-gray-400">
                                  OC {d.oficina_contable} · OG {d.organo_gestor} · UT {d.unidad_tramitadora}{d.organo_proponente ? ` · OP ${d.organo_proponente}` : ''}
                                </span>
                                {d.ya_existe && <span className="text-green-600">ya existe</span>}
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 flex justify-end">
                    <button
                      onClick={aplicarImportFace}
                      disabled={importLoading}
                      className="flex items-center gap-2 px-4 py-2 bg-duetto-primary text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
                      data-testid="face-aplicar"
                    >
                      <CheckCircle className="w-4 h-4" />
                      {importLoading ? 'Importando…' : 'Importar destinos seleccionados'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Ajustes;
